import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { DESKTOP_GROUP, MOBILE_GROUP } from '../_shared/device.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
import { mediaOverlayFields, mobileMediaOverlayFields } from '../_shared/mediaOverlay.js';
import { CAPTION_GROUP, mediaCaptionFields, mobileMediaCaptionFields, } from '../_shared/mediaCaption.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { ASPECT_RATIO_OPTIONS, MOBILE_ASPECT_INFO, mobileAspectOptions, } from '../_shared/aspect.js';
/**
 * video — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Video" section (shrine-pro-ref/sections/video.liquid).
 * A centred highlight heading over a single deferred YouTube/Vimeo embed: the SSR markup ships only a poster
 * (cover image or the neutral placeholder) + a play button + a hidden `<template>`; the iframe (and its
 * third-party bytes) load only on click (Shrine's `deferred-media`, ported to `deferredMediaInit`). Keeps a
 * top PageSpeed score by construction.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→richtext (bold/italic markup → the two highlight
 * engines), `video_url` (a youtube/vimeo picker)→text (the seller pastes the watch/share URL; the component
 * parses platform + id), image_picker→image, text→text, checkbox→checkbox, select→select, color→color,
 * color_background→gradient, header→group, visible_if→visibleWhen. Ids are clean camelCase; labels / options /
 * defaults stay faithful to Shrine (the 6 color schemes + custom palette, the 13-pattern background, the
 * heading-size scale, mobile-=×0.75 padding).
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged): (a) an UPLOADED video source — media upload is still
 * a repo-wide stub (see spec 0020), and Shrine's Video section itself is URL-only (youtube/vimeo); (b) the
 * custom mobile background IMAGE crop only kicks in when a custom image background is set (same as collage).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
/**
 * "The file decides" — the shape the section has always had: the cover image's proportions, else the
 * uploaded file's own, else 16:9. Keeping it as the DEFAULT is what makes the shape field free to add to
 * sections that are already live.
 */
const AUTO_SHAPE = { value: 'auto', label: 'Automatic (from the file)' };
const CONTENT = 'Content';
const VIDEO = 'Video';
const COLOR = 'Color';
export const videoSchema = {
    type: 'video',
    name: 'Video',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Content: heading + the two highlight engines + size ───────────────────────
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: CONTENT,
            default: { format: 'html', html: '<p>Video</p>' },
            info: 'Apply Bold (B) to a word for highlight 1, Italic (I) for highlight 2.',
        },
        ...headingHighlightFields(1, CONTENT),
        ...headingHighlightFields(2, CONTENT),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: CONTENT,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: CONTENT,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: CONTENT,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── Video: an UPLOADED file (primary) or a YouTube/Vimeo URL (fallback) + cover + full width ──
        {
            id: 'video',
            type: 'video',
            label: 'Video',
            group: VIDEO,
            info: 'Upload a video file (MP4 or WebM). Takes priority over the URL below. Loads only on click.',
        },
        {
            id: 'videoUrl',
            aiWritable: false, // 0059 — machine format, not prose
            type: 'text',
            label: 'Or a video URL',
            group: VIDEO,
            // Vide par défaut (2026-09-16) : une URL de démo faisait démarrer la section sur une vidéo YouTube
            // qui n'est pas celle de la boutique. Champ vide ⇒ placeholder scénique jusqu'au collage du lien.
            default: '',
            placeholder: 'https://www.youtube.com/watch?v=...',
            info: 'Embed a YouTube or Vimeo link instead. Used only when no file is uploaded above.',
        },
        // Lecture automatique + son (18/09/2026, fondatrice : « on devrait avoir un réglage pour la lecture
        // automatique et couper le son — on ne les a pas, donc on montre ça »). « Ça », c'était la barre de
        // lecture du navigateur : la section ne savait faire qu'un film qu'on lance, là où un clip produit de
        // cinq secondes doit se comporter comme une image qui bouge. Deux cases et non une (contrairement à
        // `mutedAutoplay` des colonnes / du carrousel) parce que le son est une décision à part : une voix off
        // s'entend, une ambiance non. Réservé à un FICHIER DÉPOSÉ — un embed YouTube/Vimeo garderait son
        // affiche, sinon la page paie le lecteur tiers à chaque visite.
        //
        // DÉFAUT PASSÉ À `true` LE 18/09/2026 (fondatrice : « always in components that have video, the play
        // automatically should be on »). Cette case était la DERNIÈRE de toutes les sections à vidéo à démarrer
        // sur « non » : colonnes, résultats, icônes+contenu, carrousel, onglets, héros, collage, image+texte —
        // toutes jouent d'elles-mêmes. Une section qui s'appelle « Vidéo » et qui affiche une image fixe était
        // l'exception que personne n'allait deviner. Le coût réseau est nul : `preload="none"` + `data-src`
        // veut dire zéro octet tant que la bande n'approche pas de l'écran, exactement comme avant.
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Play automatically',
            group: VIDEO,
            default: true,
            aiConfigurable: true,
            visibleWhen: { field: 'video', empty: false },
            info: 'The uploaded file plays on its own, looping, like a moving picture. It still loads nothing until the visitor scrolls to it. Turn it off to show a cover image the visitor clicks to start.',
        },
        {
            id: 'muted',
            type: 'checkbox',
            label: 'Mute the sound',
            group: VIDEO,
            default: true,
            visibleWhen: [
                { field: 'video', empty: false },
                { field: 'autoplay', equals: true },
            ],
            info: 'Browsers never start a video with sound on their own. Turn this off and the visitor gets the play, sound and fullscreen buttons instead, styled with your theme.',
        },
        // Le disque lecture/pause, allumé ou éteint pour CETTE vidéo (19/09/2026) — voir
        // `_shared/videoPlayButton.ts`. ÉTEINT PAR DÉFAUT depuis le 20/09/2026 (fondatrice : « by default it
        // shouldn't show ») : une vidéo posée dans une page est d'abord une image qui bouge, et c'est la
        // vendeuse qui décide d'y stamper une commande.
        //
        // Le champ ne dépend PLUS de `autoplay` : accroché à lui, il disparaissait de l'inspecteur dès que la
        // section passait en « clic pour lire » — et une vendeuse qui ne voit pas le réglage conclut qu'il
        // n'existe pas. Il reste visible ; c'est `showsPlayButton` qui garde le disque quand le clip attend
        // un geste du visiteur, et le texte d'aide le dit.
        playButtonField({
            defaultOn: false,
            group: VIDEO,
            visibleWhen: { field: 'video', empty: false },
        }),
        {
            id: 'coverImage',
            type: 'image',
            label: 'Cover image',
            group: VIDEO,
            info: 'Shown before the video loads. If empty, the still from the uploaded file is used, then a neutral placeholder.',
        },
        {
            id: 'description',
            type: 'text',
            label: 'Video alt text',
            group: VIDEO,
            info: 'Describes the video for screen readers and SEO (the "Load video" label).',
        },
        // ── Text over the media (19/09/2026) ─────────────────────────────────────────
        // The `heading` above is a title for the BAND: centred, above the clip, in the section's own colours.
        // This is a different thing — words ON the frame, anchored to one of its nine spots. The default sits
        // bottom-left and not in the middle because the middle is where the round play button lives.
        ...mediaCaptionFields(CAPTION_GROUP, 'bottom-left'),
        // ── Desktop ──────────────────────────────────────────────────────────────────
        {
            id: 'fullWidth',
            type: 'checkbox',
            label: 'Make section full width',
            group: DESKTOP_GROUP,
            default: false,
        },
        // The SHAPE of the video box (2026-09-18). Until now it was dictated by the cover image, so a seller
        // with a vertical 9:16 clip and a wide poster got a letterboxed 16:9 box — and no way to say otherwise.
        // See _shared/aspect.ts for the list, and for why a phone needs its own entry.
        {
            id: 'aspect',
            type: 'select',
            label: 'Shape',
            group: DESKTOP_GROUP,
            default: 'auto',
            aiConfigurable: true,
            info: 'The box the video plays in. Automatic follows the cover image, else the file’s own proportions. Any other shape crops to match — a vertical clip wants 9:16.',
            options: [AUTO_SHAPE, ...ASPECT_RATIO_OPTIONS],
        },
        // ── Overlay (2026-09-18): the tint/fade laid over the cover, so the poster dissolves into the band
        // it sits on instead of ending on a hard edge. Shared engine — see _shared/mediaOverlay.ts.
        ...mediaOverlayFields(DESKTOP_GROUP),
        // Shrine's own scale (36 px, 0–100), kept literal rather than taken from `paddingFields`: the universal
        // one starts at 48 and runs to 160, and a section rebuilt 1:1 from Shrine keeps Shrine's numbers.
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: DESKTOP_GROUP,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: DESKTOP_GROUP,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // ── Mobile ───────────────────────────────────────────────────────────────────
        // A SEPARATE FILE FOR THE PHONE (18/09/2026, founder). A 16:9 clip shot for a desktop band is a
        // 45px letterbox slit on a 390px screen; the shape control alone cannot fix that, because cropping a
        // wide frame to 9:16 throws away the sides the clip was composed around. So the phone gets its own
        // file — and, until one is uploaded, keeps playing the desktop one exactly as it does today.
        {
            id: 'mobileVideo',
            type: 'video',
            label: 'Video',
            group: MOBILE_GROUP,
            info: 'Optional. Played under 750px instead of the video above — the place for a vertical cut. Leave empty to use the same file.',
        },
        {
            id: 'mobileCoverImage',
            type: 'image',
            label: 'Cover image',
            group: MOBILE_GROUP,
            info: 'Optional. The still shown before the mobile video loads. Empty falls back to the desktop cover, then to the file’s own still.',
        },
        {
            id: 'mobileAspect',
            type: 'select',
            label: 'Shape',
            group: MOBILE_GROUP,
            default: 'inherit',
            aiConfigurable: true,
            info: MOBILE_ASPECT_INFO,
            options: mobileAspectOptions(AUTO_SHAPE),
        },
        // A SELECT and not a switch: a switch has two answers and this control needs three — the third being
        // "don't decide, follow the desktop", which is what every page already published is saying today.
        {
            id: 'mobileFullWidth',
            type: 'select',
            label: 'Section width',
            group: MOBILE_GROUP,
            default: 'inherit',
            aiConfigurable: true,
            info: 'A phone screen has almost no margin to spare, so a video that sits inside the page gutter on a desktop often wants the full width here.',
            options: [
                { value: 'inherit', label: 'Same as desktop' },
                { value: 'full', label: 'Full width' },
                { value: 'contained', label: 'Inside the page margins' },
            ],
        },
        ...mobileMediaOverlayFields(MOBILE_GROUP),
        ...mobileMediaCaptionFields(MOBILE_GROUP),
        // The phone's OWN margins. Until today it had none: a phone was handed 75 % of the desktop padding,
        // full stop. An absent value still means exactly that — the control says so in as many words — so
        // this lands on live storefronts without moving a pixel.
        {
            id: 'mobilePaddingTop',
            type: 'range',
            label: 'Top padding',
            group: MOBILE_GROUP,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            inheritFrom: 'paddingTop',
            inheritLabel: 'Automatic (75% of the desktop)',
        },
        {
            id: 'mobilePaddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: MOBILE_GROUP,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            inheritFrom: 'paddingBottom',
            inheritLabel: 'Automatic (75% of the desktop)',
        },
        // ── Color: the 6 Shrine schemes + a custom palette (same engine as collage) ────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: COLOR,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: COLOR,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: COLOR,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: COLOR,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: COLOR,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: COLOR,
            default: hex('#121212'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine) ─────────────────────────────
        ...patternFields('Background pattern'),
        // ── Image corners (the padding moved up into the Desktop / Mobile parts) ─────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    maxBlocks: 0,
};
export const videoPresets = [
    {
        name: 'Video',
        settings: {
            heading: { format: 'html', html: '<p>Video</p>' },
            headingSize: 'h1',
            // Aucune vidéo de démo (direction fondatrice 2026-09-16, même règle que les photos) : la section
            // s'insère sur le placeholder scénique, pas sur un clip YouTube qui n'appartient pas à la boutique.
            aspect: 'auto',
            // Every mobile setting starts on "same as desktop": one design until the seller asks for two.
            mobileAspect: 'inherit',
            mobileFullWidth: 'inherit',
            mobileMediaOverlayStyle: 'inherit',
            colorScheme: 'background-1',
            mediaOverlayStyle: 'none',
            // No caption text: the clip arrives bare, and the words only exist once the seller writes them.
            mediaCaptionPosition: 'bottom-left',
            mediaCaptionBackdrop: 'shadow',
            mobileMediaCaptionPosition: 'inherit',
            paddingTop: 36,
            paddingBottom: 36,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const videoMeta = {
    label: 'Video',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map